/**
 * Task / notice / notification files under storage/app/public/tasks.
 * On production (edupe.co.in) use same-origin /storage/tasks/... which Vercel
 * rewrites to the backend — works for <img> thumbnails over HTTPS.
 */
export const getTaskStorageUrl = (filename) => {
  if (!filename || filename === 'undefined' || filename === 'null') {
    return null;
  }
  const clean = String(filename).replace(/^\/+/, '');

  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    const isProdHttps =
      protocol === 'https:' &&
      hostname !== 'localhost' &&
      !hostname.includes('127.0.0.1') &&
      !hostname.includes('192.168.');
    if (isProdHttps) {
      return `/storage/tasks/${clean}`;
    }
  }

  return `http://3.111.220.78/api/storage/tasks/${clean}`;
};

// Utility function to handle image URLs for both development and production
export const getImageUrl = (imagePath) => {
  // Handle null, undefined, or empty string
  if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
    console.warn('getImageUrl: Received invalid image path:', imagePath);
    return '/dummy.jpg'; // Return a fallback image
  }
  
  // Remove any leading slashes
  const cleanPath = imagePath.replace(/^\/+/, '');
  
  // More robust environment detection
  let isProduction = false;
  
  if (typeof window !== 'undefined') {
    // Check multiple indicators for production
    const hostname = window.location.hostname;
    const href = window.location.href;
    const protocol = window.location.protocol;
    
    // More comprehensive production detection
    isProduction = 
      hostname.includes('vercel.app') ||
      hostname.includes('netlify.app') ||
      hostname.includes('herokuapp.com') ||
      hostname.includes('firebaseapp.com') ||
      hostname.includes('github.io') ||
      (protocol === 'https:' && hostname !== 'localhost' && !hostname.includes('127.0.0.1') && !hostname.includes('192.168.'));
    
    // Debug logging
    console.log('getImageUrl debug:', {
      imagePath,
      cleanPath,
      isProduction,
      hostname,
      href,
      protocol,
      NODE_ENV: process.env.NODE_ENV,
      userAgent: navigator.userAgent
    });
  } else {
    // Server-side rendering
    isProduction = process.env.NODE_ENV === 'production';
  }
  
  // In production (Vercel), use the serverless proxy function
  if (isProduction) {
    const proxyUrl = `/api/proxy?path=${encodeURIComponent(cleanPath)}`;
    console.log('Using serverless proxy URL:', proxyUrl);
    return proxyUrl;
  }
  
  // In development, use direct HTTP URL
  const devUrl = `http://3.111.220.78/api/storage/tasks/${cleanPath}`;
  console.log('Using development URL:', devUrl);
  return devUrl;
};

// Function to convert image to base64 as fallback
export const convertImageToBase64 = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/jpeg');
      resolve(dataURL);
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    img.src = imageUrl;
  });
};

// Enhanced image URL with fallback to base64
export const getImageUrlWithBase64Fallback = async (imagePath) => {
  const url = getImageUrl(imagePath);
  
  if (typeof window !== 'undefined') {
    try {
      // Try to load the image and convert to base64 if it fails
      const base64Url = await convertImageToBase64(url);
      console.log('Successfully converted image to base64');
      return base64Url;
    } catch (error) {
      console.warn('Failed to convert image to base64, using original URL:', error);
      return url;
    }
  }
  
  return url;
};

// Fallback function for images that fail to load
export const getImageUrlWithFallback = (imagePath) => {
  if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
    return '/dummy.jpg';
  }
  
  const cleanPath = imagePath.replace(/^\/+/, '');
  
  // Use the same environment detection logic
  let isProduction = false;
  
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    isProduction = 
      hostname.includes('vercel.app') ||
      hostname.includes('netlify.app') ||
      hostname.includes('herokuapp.com') ||
      hostname.includes('firebaseapp.com') ||
      hostname.includes('github.io') ||
      (protocol === 'https:' && hostname !== 'localhost' && !hostname.includes('127.0.0.1') && !hostname.includes('192.168.'));
  } else {
    isProduction = process.env.NODE_ENV === 'production';
  }
  
  if (isProduction) {
    // Use serverless proxy function
    return `/api/proxy?path=${encodeURIComponent(cleanPath)}`;
  }
  
  return `http://3.111.220.78/api/storage/tasks/${cleanPath}`;
};

// Utility function to handle company logo URLs
export const getCompanyLogoUrl = (logoPath) => {
  return getImageUrl(logoPath);
};

// Utility function to handle student photo URLs
export const getStudentPhotoUrl = (photoPath) => {
  return getImageUrl(photoPath);
}; 